// combatpro/views/LastActionsView.js
//
// Stage 77 (Last Actions View): migrated from the Stage 33 placeholder shell
// (which rendered state.lastActionsItems.map - the Stage 24 empty placeholder)
// to the legacy active-monster Last Actions list (battletracker.html
// vi_renderLastActions 9857-9893).
//
// Renders ONLY the active monster (state.currentCombatantIndex) into
// #vi-last-actions-mount; empty mount when no active monster - matching the
// per-monster view pattern. Legacy reparented last-actions-block-${idx} into
// the mount (vi_mountLastActions 9949) and rendered via innerHTML; both are
// replaced by declarative React reading monster.lastActions (the per-monster
// store added in Stage 77, owned by CombatContext.addLastAction).
//
// This is NOT the global Combat Log (combatLogEntries / CombatLogPanel) - that
// is a separate feed and is deliberately not touched here.
//
// Parity with vi_renderLastActions:
//   - empty list -> "No recent actions" (legacy 9863).
//   - typeMap (6 entries, 9866-9873): attack/roll/ability/spell/damage/heal ->
//     upper-case label; any other type -> 'ACTION' (9876).
//   - label: trimmed non-empty string, else 'Unknown Action' (9877).
//   - value: a.value when present, else a.total fallback, else none (9878).
//   - value display: damage -> '-' + value; heal -> '+' + value; else the bare
//     value; omitted entirely when there is no value (9880-9888).
//   - row markup mirrors 9885-9891 (vi-last-action-item > vi-last-action-top
//     [type span + optional value span] > vi-last-action-label).
//
// The feeder call sites that would populate this list (damage / spell / ability
// / weapon / attack) remain DEFERRED as of Stage 77, so the active monster
// shows "No recent actions" until a later stage wires addLastAction into those
// handlers. That is faithful to the current migration state.
//
// No getElementById, no innerHTML, no reparenting, no localStorage, no optional
// chaining, no em dash in source.

import React, { useContext } from 'react';
import { CombatContext } from '../contexts/CombatContext';

var TYPE_MAP = {
  attack: 'ATTACK',
  roll: 'ROLL',
  ability: 'ABILITY',
  spell: 'SPELL',
  damage: 'DAMAGE',
  heal: 'HEAL'
};

function resolveValueDisplay(action) {
  // a.value when non-null, else legacy a.total fallback, else null (9878).
  var rawValue = null;
  if (action.value !== undefined && action.value !== null) {
    rawValue = action.value;
  } else if (action.total !== undefined && action.total !== null) {
    rawValue = action.total;
  }
  if (rawValue === null || rawValue === undefined) {
    return '';
  }
  if (action.type === 'damage') {
    return '-' + rawValue;
  }
  if (action.type === 'heal') {
    return '+' + rawValue;
  }
  return String(rawValue);
}

function LastActionsView() {
  const { state } = useContext(CombatContext);

  const index = state.currentCombatantIndex;
  const monster = (index !== null && index !== undefined && state.monsters)
    ? state.monsters[index]
    : null;

  // No active monster: empty mount, faithful to the per-monster view pattern.
  if (!monster) {
    return (
      <div className="vi-view" id="view-last-actions">
        <div id="vi-last-actions-mount"></div>
      </div>
    );
  }

  const actions = Array.isArray(monster.lastActions) ? monster.lastActions : [];

  return (
    <div className="vi-view" id="view-last-actions">
      <div id="vi-last-actions-mount">
        <div id={'last-actions-block-' + index} className="vi-last-actions-block">
          <div id={'last-actions-list-' + index} className="vi-last-actions-list">
            {actions.length === 0 ? (
              <div className="vi-empty">No recent actions</div>
            ) : (
              actions.map((a, i) => {
                if (!a || typeof a !== 'object') { return null; }
                var typeDisplay = TYPE_MAP[a.type] ? TYPE_MAP[a.type] : 'ACTION';
                var label = (a.label && typeof a.label === 'string' && a.label.trim())
                  ? a.label.trim()
                  : 'Unknown Action';
                var valueDisplay = resolveValueDisplay(a);
                return (
                  <div className="vi-last-action-item" key={i}>
                    <div className="vi-last-action-top">
                      <span className="vi-last-action-type">{typeDisplay}</span>
                      {valueDisplay !== '' ? (
                        <span className="vi-last-action-value">{valueDisplay}</span>
                      ) : null}
                    </div>
                    <div className="vi-last-action-label">{label}</div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default LastActionsView;
